import { useEffect, useId, useRef, useState } from 'react';
import { IMAGE_TYPES } from '../api';
import { ImageIcon } from '../icons';
import { useEditorApi } from './editorApi';
import { useInspectorReadOnly } from './InspectorKit';

// 44px buttons on touch screens; a mouse keeps the compact size.
const TAP = '[@media(pointer:coarse)]:min-h-11';

/** Photos are resized in the browser before they upload: the longest side at
 *  most this many pixels... */
export const MAX_IMAGE_SIDE = 2400;
/** ...and anything still bigger than this is re-encoded (JPEG, or WebP when it
 *  may have transparency). */
export const REENCODE_OVER_BYTES = 1.5 * 1024 * 1024;

/** The size an image should be drawn at: the longest side capped at `max`. Pure. */
export function fitWithin(width: number, height: number, max = MAX_IMAGE_SIDE): { width: number; height: number; scaled: boolean } {
  const long = Math.max(width, height);
  if (!(long > max)) return { width, height, scaled: false };
  const k = max / long;
  return { width: Math.max(1, Math.round(width * k)), height: Math.max(1, Math.round(height * k)), scaled: true };
}

const EXT: Record<string, string> = { 'image/jpeg': '.jpg', 'image/webp': '.webp', 'image/png': '.png' };

/** The file name with the extension of `type` ("hero.png" as WebP: "hero.webp"). */
export function renamedFor(name: string, type: string): string {
  const base = (name || 'image').replace(/\.[^./]+$/, '') || 'image';
  return `${base}${EXT[type] ?? ''}`;
}

function encode(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    try { canvas.toBlob((b) => resolve(b), type, quality); } catch { resolve(null); }
  });
}

/**
 * Makes a picked or pasted image ready to upload: a photo with a side over
 * 2400px is scaled down, and an image over 1.5 MB is re-encoded (WebP for PNG
 * and WebP sources, which may be transparent, JPEG otherwise; JPEG when the
 * browser cannot write WebP). SVG and GIF (animation) go as they are, and so
 * does anything the browser cannot decode. Never makes a file bigger.
 */
export async function prepareImage(file: File): Promise<{ blob: Blob; name: string }> {
  const asIs = { blob: file as Blob, name: file.name };
  if (!['image/png', 'image/jpeg', 'image/webp', 'image/avif'].includes(file.type)) return asIs;
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return asIs;
  let bitmap: ImageBitmap;
  try { bitmap = await createImageBitmap(file); } catch { return asIs; }
  const fit = fitWithin(bitmap.width, bitmap.height);
  if (!fit.scaled && file.size <= REENCODE_OVER_BYTES) { bitmap.close?.(); return asIs; }
  const canvas = document.createElement('canvas');
  canvas.width = fit.width;
  canvas.height = fit.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) { bitmap.close?.(); return asIs; }
  ctx.drawImage(bitmap, 0, 0, fit.width, fit.height);
  bitmap.close?.();
  const mayBeClear = file.type === 'image/png' || file.type === 'image/webp';
  let out = await encode(canvas, mayBeClear ? 'image/webp' : 'image/jpeg', 0.85);
  // No WebP encoder (some Safari versions answer PNG instead): a big one goes JPEG.
  if (out && out.type !== 'image/webp' && mayBeClear && out.size > REENCODE_OVER_BYTES) out = await encode(canvas, 'image/jpeg', 0.85);
  if (!out || !out.size || (!fit.scaled && out.size >= file.size)) return asIs;
  return { blob: out, name: renamedFor(file.name, out.type) };
}

interface Props {
  value: string;
  onChange: (url: string) => void;
  label?: string;
  placeholder?: string;
  /** false drops the Remove control, for a list row whose own Remove already
   *  takes the image out (logos, gallery images, avatars). One remove per image. */
  clearable?: boolean;
}

/** Image field: a thumbnail, a URL field (type or paste a link, or paste an
 *  image straight from the clipboard), Upload, and one Remove. Stores the
 *  uploaded image's link (relative, /api/public/pages/img/..., so it works on
 *  every host that serves the page), the same string field the renderer reads. */
export default function ImageInput({ value, onChange, label, placeholder = 'Paste an image or a URL', clearable = true }: Props) {
  const api = useEditorApi();
  const readOnly = useInspectorReadOnly();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // A URL whose preview failed to load: show the empty slot, not a broken image.
  const [failed, setFailed] = useState('');
  const uid = useId();
  const labelId = `${uid}-label`;
  // An upload finishes seconds later. By then the page may have changed (more
  // typing, another image in the same list), so it hands its link to the
  // NEWEST onChange, which builds on the current section. When this field is
  // gone (another section was picked: the pane is keyed by section), it falls
  // back to the onChange of when the upload started, which still patches its
  // own section by id.
  const latest = useRef(onChange);
  latest.current = onChange;
  const mounted = useRef(true);
  const uploadRequest = useRef(0);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  function changeUrl(url: string) {
    uploadRequest.current++;
    setBusy(false); setErr('');
    onChange(url);
  }

  async function uploadFile(file: File) {
    if (readOnly) return;
    if (!IMAGE_TYPES.includes(file.type)) { setErr('Use a PNG, JPG, WebP, GIF, AVIF or SVG image.'); return; }
    const request = ++uploadRequest.current;
    const atStart = onChange;
    setBusy(true); setErr('');
    try {
      const ready = await prepareImage(file);
      if (request !== uploadRequest.current) return;
      const { url } = await api.uploadPageImage(ready.blob, ready.name);
      if (request !== uploadRequest.current) return;
      (mounted.current ? latest.current : atStart)(url);
    } catch (e) {
      if (mounted.current && request === uploadRequest.current) setErr(e instanceof Error && e.message ? e.message : 'The upload didn’t work. Try again.');
    } finally { if (mounted.current && request === uploadRequest.current) setBusy(false); }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (file) await uploadFile(file);
  }

  // Paste an image straight from the clipboard (Cmd/Ctrl+V), e.g. a copied logo
  // or screenshot. Falls through to normal text paste when there's no image.
  async function onPaste(e: React.ClipboardEvent) {
    if (readOnly) return;
    const item = Array.from(e.clipboardData?.items ?? []).find((it) => it.type.startsWith('image/'));
    if (!item) return; // let a pasted URL string land in the text input normally
    e.preventDefault();
    const file = item.getAsFile();
    if (file) await uploadFile(file);
  }

  return (
    <div className="grid min-w-0 gap-1.5" onPaste={onPaste}
      role={label ? 'group' : undefined} aria-labelledby={label ? labelId : undefined}>
      {label && <span id={labelId} className="label !mb-0">{label}</span>}
      <div className="flex min-w-0 items-center gap-2.5">
        <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-md border border-border bg-surface2 text-textDim">
          {value && value !== failed
            ? <img src={value} alt="" onError={() => setFailed(value)} className="h-full w-full object-cover" />
            : <ImageIcon size={20} />}
        </div>
        <div className="grid min-w-0 flex-1 gap-1.5">
          <input className="input" value={value} placeholder={placeholder}
            aria-labelledby={label ? labelId : undefined} aria-label={label ? undefined : 'Image URL'}
            onChange={(e) => changeUrl(e.target.value)} />
          {!readOnly && (
            <div className="flex items-center gap-1.5">
              <input ref={fileRef} type="file" accept={IMAGE_TYPES.join(',')} className="hidden" tabIndex={-1} aria-label="Image file" onChange={onFile} />
              <button type="button" className={`btn-ghost btn-sm ${TAP}`} disabled={busy} onClick={() => fileRef.current?.click()}>
                {busy ? 'Uploading…' : value ? 'Replace' : 'Upload'}
              </button>
              {value && clearable && <button type="button" className={`btn-ghost btn-sm ${TAP}`} onClick={() => changeUrl('')}>Remove</button>}
            </div>
          )}
        </div>
      </div>
      {err && <span role="alert" className="text-xs text-danger">{err}</span>}
    </div>
  );
}
